import * as THREE from "three";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const BRAND = {
	emerald: 0x0e7a5c,
	emeraldLight: 0x399279,
	emeraldDeep: 0x094f3c,
	gold: 0xe0a526,
	goldLight: 0xebc572,
	white: 0xffffff,
};

const REDUCED_MOTION = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const IS_TOUCH = window.matchMedia("(hover: none)").matches;

/* ------------------------------------------------------------------ i18n */

const I18N = {
	en: {
		nav_cta: "Join the waitlist",
		nav_cta_short: "Join now",
		badge: "Coming soon · Launching across the Middle East",
		hero_1: "Smart books",
		hero_2: "for smart business.",
		lead: "A cloud accounting platform built from the ground up for the Gulf, Egypt and the wider Middle East — e-invoicing, VAT, payroll, inventory and live reports. Every riyal in its place.",
		days: "Days",
		hours: "Hours",
		minutes: "Minutes",
		seconds: "Seconds",
		email_label: "Your email",
		form_cta: "Reserve my spot",
		hint: "No spam. We'll email you once, on launch day.",
		float_1_t: "E-invoice",
		float_1_s: "Issued & cleared ✓",
		float_2_t: "VAT return",
		float_2_s: "Ready in one click",
		float_3_t: "Bank reconciliation",
		float_3_s: "128 transactions matched",
		feat_eyebrow: "What we're building",
		feat_title: "Everything your business needs. In one place.",
		feat_sub: "Full details soon… here is a first glimpse.",
		soon: "Soon",
		f1_t: "E-invoicing",
		f1_d: "Tax invoices with QR codes, designed for e-invoicing rules across the region.",
		f2_t: "VAT, handled",
		f2_d: "Automatic tax calculation and filing-ready returns. No spreadsheets, no stress.",
		f3_t: "Payroll & HR",
		f3_d: "Payroll runs, leave, attendance and end-of-service — in a single click.",
		f4_t: "Inventory & sales",
		f4_d: "Multi-warehouse, point of sale and pricing that knows your margin in real time.",
		f5_t: "Scan the receipt. Done.",
		f5_d: "Snap a receipt and it becomes a categorised journal entry automatically.",
		f6_t: "Live reports",
		f6_d: "P&L, cash flow and balance sheet — on your phone, any time.",
		show_eyebrow: "Numbers you can trust",
		show_title_1: "Every riyal",
		show_title_2: "in its place.",
		show_sub: "Every transaction recorded, matched and approved automatically. You focus on growth — Ehsebly handles the rest.",
		p1: "Automatic double-entry bookkeeping",
		p2: "Fully bilingual, right-to-left native",
		p3: "Multi-currency for every country in the region",
		p4: "Granular permissions and bank-grade security",
		preview: "Preview",
		k_income: "Income",
		k_expense: "Expenses",
		k_vat: "VAT due",
		l1: "Invoice #1042 · Al Nukhba Est.",
		l2: "October payroll",
		l3: "Bank transfer · matched",
		l4: "Q3 VAT return",
		region_eyebrow: "Made for the region",
		region_title: "From Riyadh to Cairo. Your language, your currency, your rules.",
		s1: "Countries at launch",
		s2: "Languages, fully",
		s3: "Cloud & secure",
		s4: "Built for the Middle East",
		final_1: "Let us count.",
		final_2: "You relax.",
		final_sub: "Be among the first to try Ehsebly and unlock early-launch perks.",
		footer: "Every riyal in its place.",
		msg_ok: "You're on the list! We'll be in touch at launch ✓",
		msg_invalid: "Please enter a valid email address.",
		msg_limit: "Too many attempts — please try again later.",
		msg_error: "Something went wrong. Please try again.",
		title: "Ehsebly · احسبلي — Coming soon",
		lang_btn: "ع",
	},
	ar: {},
};

function captureArabic() {
	document.querySelectorAll("[data-i18n]").forEach((el) => {
		const key = el.dataset.i18n;
		if (!(key in I18N.ar)) I18N.ar[key] = el.textContent.trim();
	});
	Object.assign(I18N.ar, {
		msg_ok: "تم! أنت الآن في قائمة الانتظار، سنراسلك عند الإطلاق ✓",
		msg_invalid: "من فضلك أدخل بريداً إلكترونياً صحيحاً.",
		msg_limit: "محاولات كثيرة — حاول مرة أخرى لاحقاً.",
		msg_error: "حدث خطأ ما، حاول مرة أخرى.",
		title: document.title,
		lang_btn: "EN",
	});
}

let lang = "ar";
const t = (key) => I18N[lang][key] ?? I18N.ar[key] ?? key;

function setLanguage(next) {
	lang = next;
	const html = document.documentElement;
	html.lang = next;
	html.dir = next === "ar" ? "rtl" : "ltr";
	document.body.dir = html.dir;
	document.title = t("title");
	document.querySelectorAll("[data-i18n]").forEach((el) => {
		el.textContent = t(el.dataset.i18n);
	});
	document.querySelectorAll("[data-split]").forEach((el) => splitWords(el));
	document.querySelectorAll(".eh-word > span").forEach((s) => gsap.set(s, { yPercent: 0 }));
	document.querySelector("[data-lang-toggle]").textContent = t("lang_btn");
	try {
		localStorage.setItem("eh-lang", next);
	} catch (e) {
		// storage unavailable (private mode) — language just isn't remembered
	}
	scene?.setSide(next);
	ScrollTrigger.refresh();
}

/* Word-level split (never per-letter: Arabic letters must stay joined). */
function splitWords(root) {
	const leaves = root.querySelectorAll("[data-i18n]").length ? root.querySelectorAll("[data-i18n]") : [root];
	leaves.forEach((leaf) => {
		const text = leaf.textContent.trim();
		leaf.textContent = "";
		text.split(/\s+/).forEach((word, i, arr) => {
			const outer = document.createElement("span");
			outer.className = "eh-word";
			const inner = document.createElement("span");
			inner.textContent = word;
			outer.appendChild(inner);
			leaf.appendChild(outer);
			if (i < arr.length - 1) leaf.appendChild(document.createTextNode(" "));
		});
	});
}

/* ------------------------------------------------------------------ 3D scene */

function capsuleBetween(ax, ay, bx, by, radius, material) {
	// coordinates are in the 120×120 SVG logo space (y down)
	const a = new THREE.Vector2(ax - 60, 60 - ay);
	const b = new THREE.Vector2(bx - 60, 60 - by);
	const length = a.distanceTo(b);
	const mesh = new THREE.Mesh(new THREE.CapsuleGeometry(radius, length, 8, 24), material);
	mesh.position.set((a.x + b.x) / 2, (a.y + b.y) / 2, 0);
	mesh.rotation.z = Math.atan2(b.y - a.y, b.x - a.x) - Math.PI / 2;
	return mesh;
}

function buildLogo() {
	const p = (x, y) => [x - 60, 60 - y];
	const shape = new THREE.Shape();
	shape.moveTo(...p(60, 0));
	shape.bezierCurveTo(...p(108, 0), ...p(120, 12), ...p(120, 60));
	shape.bezierCurveTo(...p(120, 108), ...p(108, 120), ...p(60, 120));
	shape.bezierCurveTo(...p(12, 120), ...p(0, 108), ...p(0, 60));
	shape.bezierCurveTo(...p(0, 12), ...p(12, 0), ...p(60, 0));

	const depth = 16;
	const bevel = 5;
	const bodyGeo = new THREE.ExtrudeGeometry(shape, {
		depth,
		bevelEnabled: true,
		bevelThickness: bevel,
		bevelSize: bevel,
		bevelSegments: 10,
		curveSegments: 64,
	});
	bodyGeo.translate(0, 0, -depth / 2);

	const body = new THREE.Mesh(
		bodyGeo,
		new THREE.MeshPhysicalMaterial({
			color: BRAND.emerald,
			roughness: 0.22,
			metalness: 0.15,
			clearcoat: 1,
			clearcoatRoughness: 0.08,
			sheen: 0.6,
			sheenColor: new THREE.Color(BRAND.emeraldLight),
		})
	);

	const front = depth / 2 + bevel;
	const white = new THREE.MeshPhysicalMaterial({ color: BRAND.white, roughness: 0.18, clearcoat: 1 });
	const gold = new THREE.MeshPhysicalMaterial({
		color: BRAND.gold,
		metalness: 1,
		roughness: 0.22,
		clearcoat: 0.6,
		emissive: new THREE.Color(BRAND.gold),
		emissiveIntensity: 0.08,
	});

	const strokes = new THREE.Group();
	// Check E: top bar, spine, bottom bar (white) + checkmark (gold)
	strokes.add(capsuleBetween(82, 32, 36, 32, 6, white));
	strokes.add(capsuleBetween(36, 32, 36, 88, 6, white));
	strokes.add(capsuleBetween(36, 88, 82, 88, 6, white));
	const check = new THREE.Group();
	check.add(capsuleBetween(50, 61, 59, 70, 5.5, gold));
	check.add(capsuleBetween(59, 70, 82, 47, 5.5, gold));
	strokes.position.z = front;
	check.position.z = front + 1.5;

	const back = strokes.clone();
	back.position.z = -front;
	back.scale.x = -1; // reads correctly when the logo turns around

	const logo = new THREE.Group();
	logo.add(body, strokes, check, back);
	logo.scale.setScalar(0.02);
	return { logo, check };
}

function buildCoin(radius) {
	const coin = new THREE.Group();
	const goldMat = new THREE.MeshPhysicalMaterial({
		color: BRAND.goldLight,
		metalness: 1,
		roughness: 0.28,
		clearcoat: 0.5,
	});
	const disc = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, radius * 0.18, 64), goldMat);
	disc.rotation.x = Math.PI / 2;
	const rim = new THREE.Mesh(new THREE.TorusGeometry(radius * 0.78, radius * 0.06, 12, 64), goldMat);
	rim.position.z = radius * 0.09;
	const rimBack = rim.clone();
	rimBack.position.z = -radius * 0.09;
	const mark = new THREE.Mesh(
		new THREE.TorusGeometry(radius * 0.32, radius * 0.07, 12, 48, Math.PI * 1.4),
		new THREE.MeshPhysicalMaterial({ color: BRAND.emerald, metalness: 0.4, roughness: 0.3 })
	);
	mark.position.z = radius * 0.1;
	coin.add(disc, rim, rimBack, mark);
	return coin;
}

function buildParticles(count) {
	const geo = new THREE.BufferGeometry();
	const positions = new Float32Array(count * 3);
	const colors = new Float32Array(count * 3);
	const emerald = new THREE.Color(BRAND.emeraldLight);
	const gold = new THREE.Color(BRAND.gold);
	const mint = new THREE.Color(0xe3f2ec);
	for (let i = 0; i < count; i++) {
		const r = 5 + Math.random() * 12;
		const theta = Math.random() * Math.PI * 2;
		const phi = Math.acos(2 * Math.random() - 1);
		positions.set([r * Math.sin(phi) * Math.cos(theta), r * Math.cos(phi) * 0.7, r * Math.sin(phi) * Math.sin(theta) - 4], i * 3);
		const roll = Math.random();
		colors.set((roll < 0.07 ? gold : roll < 0.27 ? mint : emerald).toArray(), i * 3);
	}
	geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
	geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));

	const sprite = document.createElement("canvas");
	sprite.width = sprite.height = 64;
	const ctx = sprite.getContext("2d");
	const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
	g.addColorStop(0, "rgba(255,255,255,1)");
	g.addColorStop(0.3, "rgba(255,255,255,0.6)");
	g.addColorStop(1, "rgba(255,255,255,0)");
	ctx.fillStyle = g;
	ctx.fillRect(0, 0, 64, 64);

	return new THREE.Points(
		geo,
		new THREE.PointsMaterial({
			size: 0.09,
			map: new THREE.CanvasTexture(sprite),
			vertexColors: true,
			transparent: true,
			opacity: 0.85,
			depthWrite: false,
			blending: THREE.AdditiveBlending,
		})
	);
}

function createScene(canvas) {
	let renderer;
	try {
		renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, powerPreference: "high-performance" });
	} catch (e) {
		return null; // no WebGL: the CSS background still carries the page
	}
	renderer.setPixelRatio(Math.min(window.devicePixelRatio, IS_TOUCH ? 1.5 : 2));
	renderer.toneMapping = THREE.ACESFilmicToneMapping;
	renderer.toneMappingExposure = 1.1;

	const scene = new THREE.Scene();
	const pmrem = new THREE.PMREMGenerator(renderer);
	scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;

	const camera = new THREE.PerspectiveCamera(35, 1, 0.1, 100);
	camera.position.set(0, 0, 9);

	const key = new THREE.DirectionalLight(0xffffff, 1.6);
	key.position.set(3, 4, 6);
	const emeraldGlow = new THREE.PointLight(BRAND.emeraldLight, 30, 20);
	emeraldGlow.position.set(-4, -2, 3);
	const goldRim = new THREE.PointLight(BRAND.gold, 16, 20);
	goldRim.position.set(4, 3, -3);
	scene.add(key, emeraldGlow, goldRim, new THREE.AmbientLight(0xffffff, 0.25));

	// rig: everything that moves together with the hero
	const rig = new THREE.Group();
	scene.add(rig);

	const { logo, check } = buildLogo();
	const logoPivot = new THREE.Group();
	logoPivot.add(logo);
	rig.add(logoPivot);

	// orbiting coins on a tilted ring
	const orbit = new THREE.Group();
	orbit.rotation.set(1.15, 0.2, -0.25);
	const coins = [];
	const COIN_COUNT = 6;
	for (let i = 0; i < COIN_COUNT; i++) {
		const coin = buildCoin(0.22 + Math.random() * 0.1);
		const angle = (i / COIN_COUNT) * Math.PI * 2;
		coin.userData = { angle, radius: 2.3 + Math.random() * 0.5, spin: 0.6 + Math.random() * 1.2, bob: Math.random() * 6 };
		orbit.add(coin);
		coins.push(coin);
	}
	rig.add(orbit);

	// glowing ledger rings
	const ringMat = new THREE.MeshBasicMaterial({ color: BRAND.emeraldLight, transparent: true, opacity: 0.35 });
	const ring1 = new THREE.Mesh(new THREE.TorusGeometry(2.0, 0.008, 8, 200), ringMat);
	const ring2 = new THREE.Mesh(
		new THREE.TorusGeometry(2.9, 0.006, 8, 200),
		new THREE.MeshBasicMaterial({ color: BRAND.gold, transparent: true, opacity: 0.22 })
	);
	ring1.rotation.set(1.2, 0.3, 0);
	ring2.rotation.set(1.05, -0.35, 0.4);
	rig.add(ring1, ring2);

	const particles = buildParticles(IS_TOUCH ? 900 : 1800);
	scene.add(particles);

	// state
	const pointer = { x: 0, y: 0, tx: 0, ty: 0 };
	const state = { side: 1, sideX: 0, scroll: 0, intro: 0, visible: true };
	const RIG_EXTENT = 6.2; // world-space diameter of logo + coin orbit + rings
	const zone = document.querySelector(".eh-hero-logo-zone");
	const compact = { on: false, centerPx: 0, scale: 1 }; // small screens: logo sits above the copy
	let width = 0;
	let height = 0;

	const unitsPerPx = () => (2 * camera.position.z * Math.tan(THREE.MathUtils.degToRad(camera.fov / 2))) / height;

	function layoutX() {
		// keep the logo opposite the hero copy on wide screens
		if (compact.on) return 0;
		return state.side * Math.min(2.6, 1.15 * (width / height));
	}

	function measureZone() {
		compact.on = width < 980 && zone && zone.offsetHeight > 0;
		if (!compact.on) return;
		const r = zone.getBoundingClientRect();
		const navBottom = document.querySelector(".eh-nav").getBoundingClientRect().bottom;
		const top = Math.max(r.top + window.scrollY, navBottom + window.scrollY);
		const zoneHeight = r.bottom + window.scrollY - top;
		compact.centerPx = top + zoneHeight / 2;
		compact.scale = (Math.min(zoneHeight * 1.05, width * 0.98) * unitsPerPx()) / RIG_EXTENT;
	}

	function resize() {
		const w = window.innerWidth;
		const h = window.innerHeight;
		// mobile browsers resize the viewport as the address bar slides; ignore those
		if (IS_TOUCH && w === width && Math.abs(h - height) < 160) return;
		width = w;
		height = h;
		renderer.setSize(width, height, false);
		camera.aspect = width / height;
		camera.updateProjectionMatrix();
		measureZone();
		state.sideX = layoutX();
		rig.scale.setScalar(compact.on ? compact.scale : 1);
	}
	resize();
	window.addEventListener("resize", resize);
	window.addEventListener("load", () => {
		measureZone(); // fonts change the layout
		rig.scale.setScalar(compact.on ? compact.scale : 1);
	});

	if (!IS_TOUCH) {
		window.addEventListener("pointermove", (e) => {
			pointer.tx = (e.clientX / window.innerWidth) * 2 - 1;
			pointer.ty = (e.clientY / window.innerHeight) * 2 - 1;
		});
	}

	document.addEventListener("visibilitychange", () => {
		state.visible = !document.hidden;
	});

	const clock = new THREE.Clock();
	function frame() {
		requestAnimationFrame(frame);
		if (!state.visible) return;
		const dt = Math.min(clock.getDelta(), 0.05);
		const time = clock.elapsedTime;
		const speed = REDUCED_MOTION ? 0.15 : 1;

		pointer.x += (pointer.tx - pointer.x) * 0.05;
		pointer.y += (pointer.ty - pointer.y) * 0.05;

		const s = state.scroll; // 0 → 1 over the page
		const heroOut = Math.min(s * 4, 1); // fades as the hero leaves

		const bob = Math.sin(time * 0.8 * speed) * 0.08;
		if (compact.on) {
			// pinned to its zone in the page, so it scrolls away with the hero
			const screenY = compact.centerPx - window.scrollY;
			rig.position.x = 0;
			rig.position.y = (height / 2 - screenY) * unitsPerPx() + bob * compact.scale;
			rig.position.z = 0;
		} else {
			rig.position.x += (state.sideX * (1 - heroOut) - rig.position.x) * 0.06;
			rig.position.y = bob + heroOut * 0.6;
			rig.position.z = -heroOut * 3.5;
		}

		logoPivot.rotation.y = Math.sin(time * 0.5 * speed) * 0.45 + pointer.x * 0.5 + s * Math.PI * 4;
		logoPivot.rotation.x = Math.sin(time * 0.35 * speed) * 0.12 + pointer.y * 0.3;
		logo.scale.setScalar(0.02 * state.intro);
		check.position.y = Math.sin(time * 2 * speed) * 0.4;

		orbit.rotation.z += dt * 0.25 * speed;
		coins.forEach((coin) => {
			const d = coin.userData;
			coin.position.set(
				Math.cos(d.angle) * d.radius * state.intro,
				Math.sin(d.angle) * d.radius * state.intro,
				Math.sin(time * 1.2 + d.bob) * 0.15
			);
			coin.rotation.y += dt * d.spin * speed;
			coin.rotation.x = Math.PI / 2;
		});
		ring1.rotation.z += dt * 0.15 * speed;
		ring2.rotation.z -= dt * 0.1 * speed;

		particles.rotation.y = time * 0.02 * speed + pointer.x * 0.08;
		particles.rotation.x = pointer.y * 0.05 + s * 0.6;

		camera.position.x = pointer.x * 0.35;
		camera.position.y = -pointer.y * 0.25;
		camera.lookAt(0, 0, 0);

		renderer.render(scene, camera);
	}
	frame();

	return {
		state,
		setSide(next) {
			// logo sits on the visual left in Arabic (copy on the right) and vice versa
			state.side = next === "ar" ? -1 : 1;
			measureZone();
			state.sideX = layoutX();
		},
		intro() {
			gsap.to(state, { intro: 1, duration: REDUCED_MOTION ? 0 : 2.2, ease: "elastic.out(1, 0.6)" });
		},
	};
}

let scene = null;

/* ------------------------------------------------------------------ UI */

function initCountdown() {
	const target = new Date(document.body.dataset.launch).getTime();
	const els = {};
	document.querySelectorAll("[data-cd]").forEach((el) => (els[el.dataset.cd] = el));
	const pad = (n) => String(n).padStart(2, "0");

	function tick() {
		let diff = Math.max(0, target - Date.now());
		const values = {
			days: Math.floor(diff / 864e5),
			hours: Math.floor((diff / 36e5) % 24),
			minutes: Math.floor((diff / 6e4) % 60),
			seconds: Math.floor((diff / 1e3) % 60),
		};
		Object.entries(values).forEach(([unit, v]) => {
			const el = els[unit];
			if (!el) return;
			const text = unit === "days" ? String(v).padStart(2, "0") : pad(v);
			if (el.textContent !== text) {
				el.textContent = text;
				if (!REDUCED_MOTION) {
					el.classList.remove("is-tick");
					void el.offsetWidth;
					el.classList.add("is-tick");
				}
			}
		});
	}
	if (!Number.isNaN(target)) {
		tick();
		setInterval(tick, 1000);
	}
}

function confettiBurst(origin) {
	if (REDUCED_MOTION) return;
	const rect = origin.getBoundingClientRect();
	const colors = ["#0E7A5C", "#E0A526", "#399279", "#EBC572", "#E3F2EC"];
	for (let i = 0; i < 46; i++) {
		const piece = document.createElement("span");
		piece.className = "eh-confetti";
		piece.style.background = colors[i % colors.length];
		piece.style.left = `${rect.left + rect.width / 2}px`;
		piece.style.top = `${rect.top + rect.height / 2}px`;
		document.body.appendChild(piece);
		const angle = Math.random() * Math.PI * 2;
		const dist = 120 + Math.random() * 220;
		gsap.to(piece, {
			x: Math.cos(angle) * dist,
			y: Math.sin(angle) * dist - 120,
			rotation: Math.random() * 720 - 360,
			opacity: 0,
			duration: 1.4 + Math.random() * 0.8,
			ease: "power3.out",
			onComplete: () => piece.remove(),
		});
	}
}

function serverMessage(data) {
	try {
		const msgs = JSON.parse(data._server_messages || "[]");
		return msgs.length ? JSON.parse(msgs[0]).message : null;
	} catch (e) {
		return null;
	}
}

function initForms() {
	const csrf = document.body.dataset.csrf;
	document.querySelectorAll("[data-waitlist]").forEach((form) => {
		const input = form.querySelector("input[type=email]");
		const button = form.querySelector("button");
		const msg = form.querySelector(".eh-form-msg");
		const show = (text, isError) => {
			msg.textContent = text;
			msg.classList.toggle("is-error", !!isError);
			msg.classList.add("is-visible");
		};

		form.addEventListener("submit", async (e) => {
			e.preventDefault();
			const email = input.value.trim();
			if (!input.checkValidity() || !email) {
				show(t("msg_invalid"), true);
				gsap.fromTo(form, { x: -8 }, { x: 0, duration: 0.5, ease: "elastic.out(1, 0.3)" });
				input.focus();
				return;
			}
			button.disabled = true;
			try {
				const headers = { Accept: "application/json", "Content-Type": "application/x-www-form-urlencoded" };
				if (csrf) headers["X-Frappe-CSRF-Token"] = csrf;
				const res = await fetch("/api/method/ehsebly.api.v1.waitlist.join", {
					method: "POST",
					headers,
					body: new URLSearchParams({ email }),
				});
				const data = await res.json().catch(() => ({}));
				if (res.ok) {
					form.classList.add("is-done");
					input.value = "";
					show(t("msg_ok"));
					confettiBurst(button);
				} else if (res.status === 429) {
					show(t("msg_limit"), true);
				} else {
					show((lang === "ar" ? null : serverMessage(data)) || t(res.status === 417 ? "msg_invalid" : "msg_error"), true);
				}
			} catch (err) {
				show(t("msg_error"), true);
			} finally {
				button.disabled = false;
			}
		});
	});
}

function initTilt() {
	if (IS_TOUCH || REDUCED_MOTION) return;
	document.querySelectorAll("[data-tilt]").forEach((el) => {
		const max = el.classList.contains("eh-device") ? 8 : 10;
		el.addEventListener("pointermove", (e) => {
			const r = el.getBoundingClientRect();
			const px = (e.clientX - r.left) / r.width;
			const py = (e.clientY - r.top) / r.height;
			el.style.setProperty("--ry", `${(px - 0.5) * max}deg`);
			el.style.setProperty("--rx", `${(0.5 - py) * max}deg`);
			el.style.setProperty("--mx", `${px * 100}%`);
			el.style.setProperty("--my", `${py * 100}%`);
		});
		el.addEventListener("pointerleave", () => {
			el.style.setProperty("--rx", "0deg");
			el.style.setProperty("--ry", "0deg");
		});
	});
}

function initMagnetic() {
	if (IS_TOUCH || REDUCED_MOTION) return;
	document.querySelectorAll(".eh-magnetic").forEach((el) => {
		el.addEventListener("pointermove", (e) => {
			const r = el.getBoundingClientRect();
			gsap.to(el, {
				x: (e.clientX - r.left - r.width / 2) * 0.25,
				y: (e.clientY - r.top - r.height / 2) * 0.35,
				duration: 0.4,
				ease: "power3.out",
			});
		});
		el.addEventListener("pointerleave", () => gsap.to(el, { x: 0, y: 0, duration: 0.7, ease: "elastic.out(1, 0.4)" }));
	});
}

function initCursor() {
	const glow = document.querySelector(".eh-cursor");
	if (!glow || IS_TOUCH) return;
	const xTo = gsap.quickTo(glow, "x", { duration: 0.6, ease: "power3" });
	const yTo = gsap.quickTo(glow, "y", { duration: 0.6, ease: "power3" });
	window.addEventListener("pointermove", (e) => {
		xTo(e.clientX);
		yTo(e.clientY);
	});
}

function initNav() {
	const nav = document.querySelector(".eh-nav");
	const update = () => nav.classList.toggle("is-scrolled", window.scrollY > 24);
	update();
	window.addEventListener("scroll", update, { passive: true });
}

function initMarquee() {
	const track = document.querySelector("[data-marquee]");
	if (track) track.innerHTML += track.innerHTML; // seamless loop
}

function animateCount(el) {
	const target = Number(el.dataset.count);
	const suffix = el.dataset.suffix || "";
	const obj = { v: 0 };
	gsap.to(obj, {
		v: target,
		duration: REDUCED_MOTION ? 0 : 2,
		ease: "power2.out",
		onUpdate: () => (el.textContent = Math.round(obj.v).toLocaleString("en-US") + suffix),
	});
}

function initScrollAnimations() {
	ScrollTrigger.create({
		start: 0,
		end: "max",
		onUpdate: (self) => scene && (scene.state.scroll = self.progress),
	});

	if (REDUCED_MOTION) {
		document.querySelectorAll("[data-count]").forEach(animateCount);
		return;
	}

	// headings: word-by-word rise
	document.querySelectorAll(".eh-section [data-split], .eh-final [data-split]").forEach((el) => {
		gsap.from(el.querySelectorAll(".eh-word > span"), {
			yPercent: 110,
			duration: 0.9,
			ease: "power4.out",
			stagger: 0.06,
			scrollTrigger: { trigger: el, start: "top 85%" },
		});
	});

	gsap.utils.toArray(".eh-eyebrow, .eh-sub, .eh-points li").forEach((el) => {
		gsap.from(el, { y: 24, opacity: 0, duration: 0.8, ease: "power3.out", scrollTrigger: { trigger: el, start: "top 90%" } });
	});

	gsap.from(".eh-card", {
		y: 80,
		opacity: 0,
		rotateX: -25,
		duration: 1,
		ease: "power3.out",
		stagger: { each: 0.1, grid: "auto", from: "start" },
		scrollTrigger: { trigger: ".eh-cards", start: "top 80%" },
	});

	// device: 3D swing in, chart draws, ledger rows tick
	const device = document.querySelector(".eh-device");
	const tl = gsap.timeline({ scrollTrigger: { trigger: device, start: "top 75%" } });
	tl.from(device, { rotateY: -35, rotateX: 18, y: 100, opacity: 0, duration: 1.3, ease: "power3.out" })
		.add(() => device.querySelectorAll("[data-count]").forEach(animateCount), "-=0.8")
		.to(".eh-chart-line", { strokeDashoffset: 0, duration: 1.6, ease: "power2.inOut" }, "-=0.9")
		.to(".eh-chart-area", { opacity: 1, duration: 1 }, "-=0.8")
		.from(".eh-ledger li", { x: lang === "ar" ? 40 : -40, opacity: 0, stagger: 0.15, duration: 0.6, ease: "power3.out" }, "-=1.2")
		.to(".eh-check svg", { strokeDashoffset: 0, stagger: 0.18, duration: 0.4, ease: "power2.out" }, "-=0.5")
		.from(".eh-check", { scale: 0.4, stagger: 0.18, duration: 0.5, ease: "back.out(3)" }, "<");

	gsap.utils.toArray(".eh-stat").forEach((el, i) => {
		gsap.from(el, {
			y: 60,
			opacity: 0,
			scale: 0.9,
			duration: 0.9,
			delay: i * 0.08,
			ease: "power3.out",
			scrollTrigger: { trigger: el, start: "top 90%", onEnter: () => animateCount(el.querySelector("[data-count]")) },
		});
	});

	gsap.from(".eh-final-card", {
		scale: 0.85,
		opacity: 0,
		rotateX: 20,
		duration: 1.2,
		ease: "power3.out",
		scrollTrigger: { trigger: ".eh-final", start: "top 80%" },
	});

	// marquee leans with scroll velocity
	const marquee = document.querySelector(".eh-marquee");
	ScrollTrigger.create({
		trigger: marquee,
		onUpdate: (self) => {
			const v = Math.max(-4, Math.min(self.getVelocity() / 300, 4));
			gsap.to(marquee, { skewY: -v * 0.4, duration: 0.4, overwrite: true });
		},
		onLeave: () => gsap.to(marquee, { skewY: 0, duration: 0.6 }),
		onLeaveBack: () => gsap.to(marquee, { skewY: 0, duration: 0.6 }),
	});

	// hero copy parallax out
	gsap.to(".eh-hero-copy", {
		y: -120,
		opacity: 0.2,
		ease: "none",
		scrollTrigger: { trigger: ".eh-hero", start: "top top", end: "bottom top", scrub: true },
	});
}

function heroIntro() {
	scene?.intro();
	if (REDUCED_MOTION) return;
	const tl = gsap.timeline({ defaults: { ease: "power4.out" } });
	tl.from(".eh-nav", { y: -40, opacity: 0, duration: 0.8 })
		.from(".eh-badge", { y: 20, opacity: 0, duration: 0.7 }, "-=0.4")
		.from(".eh-hero .eh-word > span", { yPercent: 115, duration: 1.1, stagger: 0.08 }, "-=0.4")
		.from(".eh-lead", { y: 24, opacity: 0, duration: 0.9 }, "-=0.7")
		.from(".eh-cd", { y: 30, opacity: 0, rotateX: -60, duration: 0.8, stagger: 0.08 }, "-=0.6")
		.from(".eh-hero .eh-form", { y: 24, opacity: 0, duration: 0.8 }, "-=0.5")
		.from(".eh-hint", { opacity: 0, duration: 0.6 }, "-=0.4")
		.from(".eh-float", { scale: 0.6, opacity: 0, duration: 0.9, stagger: 0.15, ease: "back.out(2)" }, "-=1");

	// floating chips drift forever
	gsap.utils.toArray(".eh-float").forEach((el, i) => {
		gsap.to(el, { y: i % 2 ? 14 : -14, rotation: i % 2 ? 1.5 : -1.5, duration: 3 + i, repeat: -1, yoyo: true, ease: "sine.inOut" });
	});
}

/* ------------------------------------------------------------------ boot */

function boot() {
	captureArabic();
	document.querySelectorAll("[data-split]").forEach(splitWords);

	scene = createScene(document.getElementById("eh-scene"));

	let saved = null;
	try {
		saved = localStorage.getItem("eh-lang");
	} catch (e) {
		// ignore
	}
	if (saved === "en") setLanguage("en");
	else scene?.setSide("ar");

	document.querySelector("[data-lang-toggle]").addEventListener("click", () => {
		const next = lang === "ar" ? "en" : "ar";
		gsap.to("main, .eh-nav", {
			opacity: 0,
			duration: 0.25,
			onComplete: () => {
				setLanguage(next);
				gsap.to("main, .eh-nav", { opacity: 1, duration: 0.35 });
			},
		});
	});

	initMarquee();
	initNav();
	initCountdown();
	initForms();
	initTilt();
	initMagnetic();
	initCursor();
	initScrollAnimations();

	const reveal = () => {
		document.body.classList.remove("is-loading");
		heroIntro();
	};
	setTimeout(reveal, REDUCED_MOTION ? 0 : 1300);
}

if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
else boot();
